import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { QueryService } from '../../comun/services/query.service';
import { GlobalConstants } from '../../global-constants';
import { FuncionesService } from '../../comun/services/funciones.service';
import { LoaderService } from '../../comun/services/loader.service';
import { UsuarioPerfil } from '../../comun/models/misdatos.model';
import { MisdatosService } from '../../comun/services/misdatos.service';

@Component({
  selector: 'app-amigos-modal',
  templateUrl: './amigos-modal.component.html',
  styleUrl: './amigos-modal.component.css'
})
export class AmigosModalComponent implements AfterViewInit, OnDestroy {
  @Input() userid = 0;
  misdatos: any = {};
  amigos: any[] = [];
  busqueda = '';
  loadamigos = false;
  errorCarga = false;
  busy = 0;
  private readonly subscriptions = new Subscription();
  private modal: HTMLElement | null = null;
  private readonly alAbrir = () => this.getMisDatos();
  private readonly alCerrar = () => { this.amigos = []; this.busqueda = ''; this.loadamigos = false; };

  constructor(private query: QueryService,
              public funciones: FuncionesService,
              private loader: LoaderService,
              private misdatosService: MisdatosService) {}

  ngAfterViewInit(): void {
    this.modal = document.getElementById('amigosModal');
    this.modal?.addEventListener('shown.bs.modal', this.alAbrir);
    this.modal?.addEventListener('hidden.bs.modal', this.alCerrar);
  }

  ngOnDestroy(): void {
    this.modal?.removeEventListener('shown.bs.modal', this.alAbrir);
    this.modal?.removeEventListener('hidden.bs.modal', this.alCerrar);
    this.subscriptions.unsubscribe();
  }

  getMisDatos(): void {
    this.busqueda = '';
    this.loadamigos = false;
    this.errorCarga = false;
    // Tomamos un único valor válido por apertura: no se acumulan suscripciones.
    const sub = this.misdatosService.misdatos$
      .pipe(filter((datos): datos is UsuarioPerfil => datos !== null), take(1))
      .subscribe(datos => {
        this.misdatos = datos;
        this.getAmigos();
      });
    this.subscriptions.add(sub);
  }

  getAmigos(): void {
    this.loadamigos = false;
    this.errorCarga = false;
    this.subscriptions.add(this.query.query('seguir', 'GET', { ID: this.userid }).subscribe({
      next: (resultado: any) => {
        this.amigos = Array.isArray(resultado?.registros)
          ? resultado.registros.map((amigo: any) => ({ ...amigo, busy: 0 })) : [];
        this.loadamigos = true;
      },
      error: () => { this.errorCarga = true; this.loadamigos = true; }
    }));
  }

  // Búsqueda local: no añade llamadas a la API ni modifica la lista original.
  private normalizar(valor: any): string {
    return String(valor ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
  }

  get amigosFiltrados(): any[] {
    const termino = this.normalizar(this.busqueda);
    if (!termino) return this.amigos;
    const palabras = termino.split(/\s+/);
    return this.amigos.filter(amigo => {
      const texto = this.normalizar([
        amigo.first_name, amigo.last_name, amigo.display_name,
        amigo?.mascota?.ma_nombre, amigo.ciudad, amigo.provincia,
        amigo?.mascota?.ma_raza
      ].filter(Boolean).join(' '));
      return palabras.every(palabra => texto.includes(palabra));
    });
  }

  get pendientes(): number { return this.amigos.filter(a => a.se_estado === 'pendiente').length; }
  get activos(): number { return this.amigos.filter(a => a.se_estado === 'activo').length; }
  trackByAmigo = (index: number, amigo: any): string =>
    String(amigo?.se_id ?? amigo?.se_us_id ?? index);

  private notificarAmistadesActualizadas(): void {
    const userId = Number(this.misdatos?.ID);
    if (!userId) return;
    window.dispatchEvent(new CustomEvent('iguau:amistades-actualizadas', {
      detail: { userId }
    }));
  }

  aceptarAmigo(perfil: any): void {
    if (perfil.busy) return;
    perfil.busy = 1;
    this.subscriptions.add(this.query.query('seguir', 'ACEPTAR', { se_id: perfil.se_id }).subscribe({
      next: () => {
        // Confirmar también el seguimiento recíproco antes de actualizar la tarjeta.
        this.subscriptions.add(this.query.query('seguir', 'SEGUIR', {
          se_us_id: this.misdatos.ID,
          se_us_id_to: perfil.se_us_id,
          se_estado: 'activo'
        }).subscribe({
          next: (resultado: any) => {
            perfil.se_estado = resultado?.estado || 'activo';
            perfil.busy = 0;
            this.notificarAmistadesActualizadas();
            const texto = '<b>' + String(this.misdatos.first_name || '') + '</b> ha aceptado tu solicitud de amistad';
            this.subscriptions.add(this.query.enviarMail({
              plantilla: 'general.html', us_id_from: this.misdatos.ID,
              us_id: perfil.se_us_id, tipo: 'ACTIVIDAD', asunto: 'Amistad en iGuau',
              contenido: texto, botonurl: GlobalConstants.urlWeb,
              boton: 'Ir a iGuau', estado: 'pendiente', contacto: ''
            }).subscribe({ error: () => {} }));
          },
          error: () => { perfil.busy = 0; this.getAmigos(); this.avisoError(); }
        }));
      },
      error: () => { perfil.busy = 0; this.avisoError(); }
    }));
  }

  async cancelarAmigo(perfil: any): Promise<void> {
    if (perfil.busy) return;
    const result = await Swal.fire({
      title: 'Dejar de seguir',
      text: '¿Quieres dejar de seguir a este perfil? Ambos perfiles dejarán de seguirse.',
      icon: 'question', showCancelButton: true,
      confirmButtonText: 'Sí, dejar de seguir', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#527B69'
    });
    if (!result.isConfirmed) return;
    perfil.busy = 1;
    this.subscriptions.add(this.query.query('seguir', 'SEGUIR', {
      se_us_id: this.misdatos.ID, se_us_id_to: perfil.se_us_id
    }).subscribe({
      next: () => {
        this.subscriptions.add(this.query.query('seguir', 'SEGUIR', {
          se_us_id: perfil.se_us_id, se_us_id_to: this.misdatos.ID
        }).subscribe({
          next: () => {
            this.amigos = this.amigos.filter(a => a.se_id !== perfil.se_id);
            perfil.busy = 0;
            this.notificarAmistadesActualizadas();
          },
          error: () => { perfil.busy = 0; this.getAmigos(); this.avisoError(); }
        }));
      },
      error: () => { perfil.busy = 0; this.avisoError(); }
    }));
  }

  private avisoError(): void {
    Swal.fire({ icon: 'error', title: 'No se pudo completar la acción',
      text: 'Inténtalo de nuevo.', confirmButtonColor: '#527B69' });
  }

  reload(): void { this.loader.show(); window.location.reload(); }

  irPerfil(perfil: any): void {
    if (perfil?.slug) window.location.href = GlobalConstants.urlServer + 'perfil/' + encodeURIComponent(perfil.slug);
  }
  goPefil(perfil: any): void { this.irPerfil(perfil); }
}
